import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useTheme, Skeleton } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import Stack from '@mui/material/Stack';
import { useSelector } from 'react-redux';
import { createSearchParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  activeCountriesSelector,
  dashboardMastersStatus2Selector,
  masterDataSelector,
} from '../../../../state';
import { useMuiPickersLocale } from '../../../../hooks';
import { getStatusColor, dateRequestMap } from '../../../../helpers';
import routes from '../../../../config/routes';
import { mastersStatusKeys } from '../../../../types';

export const ItemsContainer = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1),
}));

export const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  '& > a': {
    width: '100%',
  },
}));

export const StyledLink = styled(Link)(() => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
}));

interface StatusButtonProps {
  importDate: Date;
  status: string;
}

const StatusButton = ({ importDate, status }: StatusButtonProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const locale = useMuiPickersLocale();
  const params = {
    [mastersStatusKeys.importDate]: dateRequestMap(importDate) as string,
    [mastersStatusKeys.status]: status || '',
  };
  const to = {
    pathname: routes.protectedNestedRoutes.mastersImport.index.path,
    search: `?${createSearchParams(params)}`,
  };
  return (
    <Container>
      <StyledLink to={to}>
        <div>{format(importDate, 'P', { locale })}</div>
        <Typography variant="body1" color={getStatusColor(status, theme)}>
          <Box component="span" fontWeight="bold">
            {t(`masters-imports.states.${status}`)}
          </Box>
        </Typography>
      </StyledLink>
    </Container>
  );
};

const IncorporationError = () => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" color="error" padding={1} spacing={1}>
      <ErrorIcon color="error" />
      <Typography variant="body1" color="error">
        {t('errors.generics.loadingData')}
      </Typography>
    </Stack>
  );
};

const MasterContent = () => {
  const { data, loading: resourceLoading, error } = useSelector(dashboardMastersStatus2Selector);
  const masterDataResource = useSelector(masterDataSelector);
  const activeCountriesResource = useSelector(activeCountriesSelector);

  const loading = resourceLoading || masterDataResource.loading || activeCountriesResource.loading;

  return (
    <ItemsContainer spacing={1}>
      {!error &&
        !loading &&
        data.map(i => (
          <StatusButton key={i.importDate.toString()} importDate={i.importDate} status={i.status} />
        ))}
      {loading && (
        <>
          <Skeleton />
          <Skeleton />
        </>
      )}
      {!loading && error && <IncorporationError />}
    </ItemsContainer>
  );
};

export default MasterContent;
